import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../hooks/useApi';
import Select from 'react-select';
import sanitizeHtml from 'sanitize-html';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const EducationalContentList = () => {
  const navigate = useNavigate();
  const { data: content, isLoading: contentLoading, error: contentError, fetchData: fetchContent } = useApi('/content');
  const { data: categories, isLoading: categoriesLoading, error: categoriesError } = useApi('/content/categories');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortOption, setSortOption] = useState({ value: 'title-asc', label: 'Title (A-Z)' });

  useEffect(() => {
    if (contentError || categoriesError) {
      if (contentError?.response?.status === 401 || categoriesError?.response?.status === 401) {
        toast.error('Authentication required. Please log in.');
        navigate('/login');
      } else {
        toast.error(contentError?.message || categoriesError?.message || 'Failed to load content');
      }
    }
  }, [contentError, categoriesError, navigate]);

  useEffect(() => {
    if (selectedCategory) {
      fetchContent(`/content/category/${selectedCategory.value}`);
    } else {
      fetchContent('/content');
    }
  }, [selectedCategory, fetchContent]);

  const handleCategoryChange = (selected) => {
    setSelectedCategory(selected);
  };

  const handleSortChange = (selected) => {
    setSortOption(selected || { value: 'title-asc', label: 'Title (A-Z)' });
  };

  const categoryOptions = categories?.map((cat) => ({
    value: cat._id,
    label: cat.name,
  })) || [];

  const sortOptions = [
    { value: 'title-asc', label: 'Title (A-Z)' },
    { value: 'title-desc', label: 'Title (Z-A)' },
    { value: 'views-desc', label: 'Most Viewed' },
    { value: 'likes-desc', label: 'Most Liked' },
  ];

  const sortedContent = useMemo(() => {
    if (!content) return [];
    const sorted = [...content];
    switch (sortOption?.value) {
      case 'title-asc':
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case 'title-desc':
        return sorted.sort((a, b) => b.title.localeCompare(a.title));
      case 'views-desc':
        return sorted.sort((a, b) => (b.engagement?.views || 0) - (a.engagement?.views || 0));
      case 'likes-desc':
        return sorted.sort((a, b) => (b.engagement?.likes || 0) - (a.engagement?.likes || 0));
      default:
        return sorted;
    }
  }, [content, sortOption]);

  const truncateText = (text, maxLength) => {
    if (!text) return 'No description provided.';
    const cleanText = sanitizeHtml(text, { allowedTags: [], allowedAttributes: {} });
    return cleanText.length > maxLength ? `${cleanText.slice(0, maxLength)}...` : cleanText;
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: 'secondary',
      '&:hover': { borderColor: 'secondary' },
      boxShadow: 'none',
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      padding: '0.25rem',
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.5rem',
      border: '1px solid secondary',
      backgroundColor: '#ffffff',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'secondary' : state.isFocused ? '#f9e8d9' : '#ffffff',
      color: state.isSelected ? '#ffffff' : '#1f2937',
      '&:hover': { backgroundColor: '#f9e8d9' },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#1f2937',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
    }),
  };

  if (contentLoading || categoriesLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary mx-auto"></div>
          <p className="mt-4 text-lg font-medium text-gray-600">Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif] py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-primary tracking-tight sm:text-5xl">
            Explore Educational Content
          </h1>
          <p className="mt-3 text-lg text-gray-500 max-w-2xl mx-auto">
            Discover a wide range of educational resources tailored to your interests.
          </p>
          <nav className="mt-4 text-sm text-gray-500">
            <a href="/" className="hover:text-secondary transition-colors">Home</a>
            <span className="mx-2">/</span>
            <span className="text-secondary">Educational Content</span>
          </nav>
        </header>

        <div className="bg-white shadow-sm rounded-lg p-6 mb-12 max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Category</label>
              <Select
                options={categoryOptions}
                value={selectedCategory}
                onChange={handleCategoryChange}
                placeholder="Select a category"
                isClearable
                styles={customSelectStyles}
                className="w-full"
                classNamePrefix="react-select"
                aria-label="Filter by category"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <Select
                options={sortOptions}
                value={sortOption}
                onChange={handleSortChange}
                placeholder="Sort by"
                styles={customSelectStyles}
                className="w-full"
                classNamePrefix="react-select"
                aria-label="Sort content"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedContent?.length > 0 ? (
            sortedContent.map((item) => (
              <div
                key={item._id}
                className="bg-white rounded-xl shadow-md overflow-hidden transform hover:scale-[1.02] transition-all duration-300 cursor-pointer"
                onClick={() => navigate(`/farming-insights/${item._id}`)}
                role="button"
                aria-label={`View details for ${item.title}`}
              >
                <img
                  src={item.previewImage?.url ? `${BASE_URL}${item.previewImage.url}` : 'https://via.placeholder.com/400x250'}
                  alt={item.title}
                  className="w-full h-48 object-cover"
                  loading="lazy"
                />
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-primary mb-3 line-clamp-2">{item.title}</h2>
                  <p className="text-sm text-gray-500 mb-2">
                    <span className="font-medium">Category:</span> {item.category?.name || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-500 mb-3">
                    <span className="font-medium">Subcategory:</span> {item.subcategory || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600 line-clamp-3">{truncateText(item.description, 120)}</p>
                  <div className="mt-4">
                    <span className="inline-block px-4 py-2 text-sm font-medium text-white bg-secondary rounded-md hover:bg-opacity-90 transition-colors">
                      Learn More
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500 text-lg col-span-1 md:col-span-2 lg:col-span-3 py-12">
              No content available at the moment.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EducationalContentList;